// {{{ FormMixin

FormMixin = {
	forceSubmitProp: Symbol(),

	isFormFieldGroupComponent(c) {
		return c instanceof FormFieldsGroup;
	},

	getFormFieldsData() {
		let data = {};

		this.traverseChildren(f => {
			if (f.name && f.getValue && (!f.isDisabled || !f.isDisabled() || f[FormMixin.forceSubmitProp])) {
				data[f.name] = f.getValue();
				return 'skip';
			}

			if (this.isFormFieldGroupComponent(f) && f.name) {
				return 'skip';
			}

			return null;
		});

		return data;
	},

	setFormFieldsData(data) {
		this.traverseChildren(f => {
			if (f.name && f.setValue) {
				f.setValue(data[f.name]);
				return 'skip';
			}

			if (this.isFormFieldGroupComponent(f) && f.name) {
				return 'skip';
			}

			return null;
		});
	},

	setFormDisabled() {
		let restoreFields = [];

		this.traverseChildren(function(f) {
			if (f.name && f.setDisabled) {
				f[FormMixin.forceSubmitProp] = f[FormMixin.forceSubmitProp] || 0;

				//XXX: temporary, will be refactored on next release
				if (!f.noSubmitWhenDisabled || !f.isDisabled()) {
					f[FormMixin.forceSubmitProp] += 1;
					restoreFields.push(() => f[FormMixin.forceSubmitProp] -= 1);
				}

				if (!f.isDisabled()) {
					restoreFields.push(() => {
						if (f[FormMixin.forceSubmitProp] == 0) {
							f.setDisabled(false);
						}
					});

					f.setDisabled(true);
				}

				return 'skip';
			}

			return null;
		});

		this.el.querySelectorAll('button').forEach(b => {
			if (!b.disabled) {
				restoreFields.push(() => b.disabled = false);
			}

			b.disabled = true;
		});

		return function() {
			for (let fn of restoreFields) {
				fn();
			}
		};
	},

	/* this updates the field validation status in the DOM */
	validateForm(updateFields = true) {
		let valid = true;

		this.traverseChildren(function(f) {
			if (f.isDisabled && f.isDisabled()) {
				return 'skip';
			}

			if (f.validate) {
				if (!f.validate(updateFields)) {
					valid = false;
				}

				return 'skip';
			}

			return null;
		});

		return valid;
	},

	reportFormError() {
		Application.notify({
			kind: 'error',
			text: _('Prosíme vyplňte formulář správně.'),
			priority: 5,
			timeout: 3000,
		});
	},

	reportFormValid() {
		if (!this.validateForm()) {
			this.reportFormError();
			return false;
		}

		return true;
	},
};

// }}}
// {{{ FormField

GW.define('FormField', 'GW.Component', {
	requiredErrorMessage: _('Toto pole je vyžadované'),
	extraClass: null,
	hint: null,

	initComponent() {
		if (this.label != null) {
			this.el = document.gwCreateElement({
				className: 'form-field',
				'attr:data-name': this.name,
				children: [{
					ref: 'labelEl',
					':skip': !this.label,
					nodeName: 'label',
					textContent: this.label,
				}, {
					ref: 'inputCt',
					className: 'input-ct',
					children: [this.renderInput()],
				}, {
					ref: 'hintCt',
					className: 'hint hidden',
				}, {
					ref: 'msgCt',
					className: 'message hidden',
				}],
			}, this);
		} else {
			this.el = document.gwCreateElement(this.renderInput(), this);
		}

		this.el.classList.addFromStringList(this.extraClass);
		this.input?.classList.addFromStringList(this.extraClass);

		if (this.value != null) {
			this.setValue(this.value);
		}

		if (this.hint) {
			this.hintCt.gwHide(false);
			this.hintCt.textContent = this.hint;
		}
	},

	setLabel(text) {
		this.label = text;

		if (this.labelEl) {
			this.labelEl.textContent = text || '';
		}
	},

	isDisabled() {
		return false;
	},

	setDisabled(v) {
	},

	isEmpty() {
		let val = this.getValue();

		return val == null || (typeof val == 'string' && val.match(/^\s*$/))
	},

	getViolation() {
		if (!this.optional && this.isEmpty()) {
			return this.requiredErrorMessage;
		}

		return null;
	},

	validate(updateMsg) {
		if (this.isDisabled()) {
			this.setError();
			return true;
		}

		let msg = this.getViolation();
		if (updateMsg) {
			this.setError(msg);
		}

		return !msg;
	},

	setError(msg) {
		if (this.msgCt) {
			this.msgCt.classList.toggle('hidden', !msg);
			this.msgCt.textContent = msg;
		}

		this.el.classList.toggle('error', !!msg);
	},
});

// }}}
// {{{ TextField

GW.define('TextField', 'FormField', {
	type: 'text',
	enforcePasswordRules: false,
	autocomplete: undefined,
	tooLongMessage: _('Text je příliš dlouhý'),
	tooShortMessage: _('Text je příliš krátký'),

	renderInput() {
		return {
			ref: 'input',
			nodeName: 'input',
			type: this.type,
			step: ['number', 'time'].includes(this.type) ? (this.step || 'any') : undefined,
			min: this.min,
			max: this.max,
			minLength: this.minLength,
			maxLength: this.maxLength,
			pattern: this.pattern,
			required: !this.optional,
			disabled: this.disabled,
			readOnly: this.readOnly,
			tabIndex: this.disabled ? '-1' : '0',
			autocomplete: this.autocomplete,
			autofocus: this.autofocus,
			placeholder: this.placeholder,
			'on:input': ev => this.fire('change'),
			'on:blur': ev => this.fixup(),
		};
	},

	'after:initComponent'() {
		if (this.enableSuggestions) {
			this.initSuggestions();
		}
	},

	'before:releaseComponent'() {
		this.destroySuggestions();
	},

	isDisabled() {
		return this.input.disabled;
	},

	setDisabled(v) {
		this.input.disabled = !!v;
		this.input.tabIndex = v ? '-1' : '0';
	},

	getViolation() {
		let val = this.getValue();

		if (!this.optional && this.isEmpty()) {
			return this.requiredErrorMessage;
		}

		if (this.type == 'password' && this.enforcePasswordRules) {
			if (val && val.length < 12) {
				return _('Heslo musí musí být dlouhé alespoň 12 znaků.');
			}
		}

		if (this.type == 'date') {
			if (val == 'invalid') {
				return _('Chybný formát datumu. Použijte d.m.r nebo m/d/r');
			}
		}

		if (this.type == 'datetime-local') {
			if (val == 'invalid') {
				return _('Chybný formát datumu a času. Použijte d.m.r h:m nebo m/d/r h:m');
			}
		}

		if (this.type == 'text') {
			if (this.input.validity && this.input.validity.tooLong) {
				return this.tooLongMessage;
			}

			if (this.input.validity && this.input.validity.tooShort) {
				return this.tooShortMessage;
			}
		}

		if (this.input.type == 'number') {
			if (this.input.validity && this.input.validity.rangeOverflow) {
				return this.getOverflowErrorMessage();
			}

			if (this.input.validity && this.input.validity.rangeUnderflow) {
				return this.getUnderflowErrorMessage();
			}

			if (!this.input.validity.valid) {
				return _('Musí být číslo');
			}
		}

		if (this.input.type == 'email') {
			if (!this.input.validity.valid) {
				return _('Chybná e-mailová adresa');
			}
		}

		if (this.input.validity && !this.input.validity.valid) {
			return _('Chybná hodnota');
		}

		return null;
	},

	getUnderflowErrorMessage() {
		return _('Hodnota je příliš nízká');
	},

	getOverflowErrorMessage() {
		return _('Hodnota je příliš vysoká');
	},

	setValue(value) {
		if (this.type == 'date') {
			if (Number.isInteger(value)) {
				// if specifying unix time, we need to make sure
				// value Date object will be in local time
				value = Utils.formatUTCDateIso(value);
			}

			value = Utils.parseDateIso(value);

			if (value instanceof Date) {
				if (this.input.type == 'date') {
					this.input.value = Utils.formatDateIso(value);
					return;
				}

				value = Formats.input_date(value);
			} else {
				value = '';
			}
		}

		if (this.type == 'datetime-local') {
			if (Number.isInteger(value) || /\d+/.exec(value)) {
				if (this.input.type == 'datetime-local') {
					let d = new Date(value * 1000);
					this.input.valueAsNumber = d.getTime() - d.getTimezoneOffset() * 1000 * 60;
					return;
				}

				value = Formats.input_datetime(value);
			} else {
				value = '';
			}
		}
		
		if (this.type == 'number') {
			if (value == null) {
				this.input.value = '';
			} else {
				this.input.value = Number(value);
			}
			return;
		}

		this.input.value = value == null ? '' : value;
	},

	fixup() {
		if (['date', 'datetime-local', 'time'].includes(this.type)) {
			this.setValue(this.getValue());
		}

		this.fire('blur');
	},

	parseUserDate(s) {
		let d = new Date();
		let ymd, m;

		d.setHours(0, 0, 0, 0);

		if (s == '') {
			return null;
		} else if (m = s.match(/^(\d+)\.(\d+)\.(\d+)$/)) {
			ymd = [Number(m[3]), Number(m[2]) - 1, Number(m[1])];
		} else if (m = s.match(/^(\d+)\/(\d+)\/(\d+)$/)) {
			ymd = [Number(m[3]), Number(m[1]) - 1, Number(m[2])];
		} else if (m = s.match(/^(\d+)\/(\d+)$/)) {
			ymd = [d.getFullYear(), Number(m[1]) - 1, Number(m[2])];
		} else if (m = s.match(/^(\d+)\.(\d+)\.?$/)) {
			ymd = [d.getFullYear(), Number(m[2]) - 1, Number(m[1])];
		} else if (m = s.match(/^(\d+)[\.\/]?$/)) {
			ymd = [d.getFullYear(), d.getMonth(), Number(m[1])];
		} else {
			return 'invalid';
		}

		d.setFullYear(...ymd);

		if (d.getFullYear() != ymd[0] || d.getMonth() != ymd[1] || d.getDate() != ymd[2]) {
			return 'invalid';
		}

		return Math.floor(d.getTime() / 1000);
	},

	parseUserDateTime(s) {
		let me = this;
		let now = new Date(), match;
		let parts = s.split(/\s+/);

		function matchTime(s) {
			if (match = s.match(/^(\d{1,2}):(\d{0,2})$/)) {
				let [whole, h, m] = match;

				now.setHours(Number(h), Number(m), 0, 0);
				return true;
			}

			return false;
		}

		function matchDate(s) {
			let date = me.parseUserDate(s);
			if (date != null && date != 'invalid') {
				let d = new Date(date * 1000);
				now.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
				return true;
			}

			return false;
		}

		function exit(v) {
			return Math.floor(v.getTime() / 1000);
		}

		now.setHours(0, 0, 0, 0);

		if (parts.length == 1) {
			if (parts[0] === '') {
				return null;
			}

			// parse out time or date
			if (matchTime(parts[0]) || matchDate(parts[0])) {
				return exit(now);
			}
		} else if (parts.length == 2) {
			// first try date time and time date variants
			if (matchTime(parts[0]) && matchDate(parts[1])) {
				return exit(now);
			}

			if (matchTime(parts[1]) && matchDate(parts[0])) {
				return exit(now);
			}

			// now try to parse out at least the date
			now.setHours(0, 0, 0, 0);

			if (matchDate(parts[0]) || matchDate(parts[1])) {
				return exit(now);
			}
		}

		return 'invalid';
	},

	getValue() {
		if (this.type == 'date') {
			// valueAsDate return UTC date
			if (this.input.valueAsDate) {
				return Utils.formatUTCDateIso(this.input.valueAsDate);
			}

			// our parsing function returns local date
			return Utils.formatDateIso(this.parseUserDate(this.input.value.trim()));
		}

		if (this.type == 'datetime-local') {
			if (this.input.valueAsNumber) {
				let d = new Date(this.input.value);

				return Math.floor(d.getTime() / 1000);
			}

			return this.parseUserDateTime(this.input.value.trim());
		}

		if (this.type == 'number') {
			if (!this.input.value.trim()) {
				return null;
			}

			let num = Number(this.input.value);

			return isNaN(num) ? 'invalid' : num;
		}

		if (this.input.value.trim() === '') {
			return null;
		}

		return this.input.value;
	},

	/*
	 * Suggestions feature
	 */

	enableSuggestions: false,

	initSuggestions() {
		this.input.addEventListener('input', ev => this.onInputChange(ev));
		this.input.addEventListener('focus', ev => this.onInputFocus(ev));
		this.input.addEventListener('blur', ev => this.onInputBlur(ev));
		this.input.addEventListener('click', ev => this.onInputClick(ev));
		this.input.addEventListener('keydown', ev => this.onInputKeydown(ev));
	},

	destroySuggestions() {
		this.suggestionsPopup && this.suggestionsPopup.destroy();
	},

	onInputChange(ev) {
		this.refreshSuggestions();
	},

	onInputFocus(ev) {
		ev.target.select();
	},

	onInputBlur(ev) {
		if (this.input.readOnly) {
			return;
		}

		this.hideSuggestions();
	},

	onInputClick(ev) {
		ev.target.select();

		this.refreshSuggestions();
	},

	onInputKeydown(ev) {
		let popup = this.suggestionsPopup;
		let popupVisible = popup && !popup.el.classList.contains('hidden');

		if (this.input.readOnly) {
			return;
		}

		if (!popupVisible) {
			switch (ev.key) {
			case 'ArrowDown':
				ev.preventDefault();
				this.refreshSuggestions();
				break;

			case 'Enter':
				ev.preventDefault();
				this.fire('submit', this.input.value);
				break;
			}

			return;
		}

		switch (ev.key) {
		case 'ArrowDown':
			ev.preventDefault();
			popup.move(+1);
			break;

		case 'ArrowUp':
			ev.preventDefault();
			popup.move(-1);
			break;

		case 'Enter':
			let value = popup.getSelectedValue();
			if (value) {
				ev.preventDefault();

				this.onSuggestionSelect(value);
			} else {
				ev.preventDefault();
				this.hideSuggestions();
				this.fire('submit', this.input.value);
			}
			break;

		case 'Escape':
			ev.preventDefault();
			this.input.value = '';
			this.hideSuggestions();
			break;
		}
	},

	onSuggestionSelect(value) {
		this.input.focus();
		this.hideSuggestions();

		this.fire('select', value);
	},

	async refreshSuggestions() {
		let query = this.input.value.trim();
		let popup = this.suggestionsPopup;
		if (!popup) {
			this.suggestionsPopup = popup = this.createSuggestionPopup();
			if (!popup) {
				throw new Error('Popup not implemented');
			}

			popup.hidePopup = function() {
				if (this.el) {
					this.el.gwHide(true);
				}
			};

			popup.on('select', c => {
				this.onSuggestionSelect(c.getSelectedValue());
			});
		}

		let visible = true;
		if (!popup.visibleLoad) {
			visible = await popup.loadData(query);
		}

		if (visible) {
			popup.el.gwHide(false);
			popup.el.style.width = this.el.offsetWidth + 'px';

			PopupManager.show(popup, {
				anchorEl: this.el,
				forceAxis: 'vertical',
				preferReadingOrder: true,
				resizeNotFitting: true,
				disableMobileUIPopover: true,
				focus: false,
			});
		}

		if (popup.visibleLoad) {
			visible = await popup.loadData(query);
		}

		if (!visible) {
			this.hideSuggestions();
		}
	},

	hideSuggestions() {
		if (this.suggestionsPopup) {
			PopupManager.hide(this.suggestionsPopup);
		}
	},

	/*
	 * This needs to be implemented by the widget user.
	 */
	createSuggestionPopup() {
	},
});

// }}}
// {{{ SuggestionsPopup

GW.define('SuggestionsPopup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'suggestions-popup',
			'on*:mousedown': ev => {
				let el = ev.target.closest('.selectable');
				if (el) {
					Utils.selectListItem(this.el, '.selectable', 'selected', i => i === el);

					this.fire('select');
				}
			},
		}, this);
	},

	getSelectedValue() {
		let el = this.getSelectedElement();

		return el ? el.$value : null;
	},

	getSelectedElement() {
		return this.el.querySelector('.selected');
	},

	move(offset) {
		let selectables = Array.from(this.el.querySelectorAll('.selectable'));
		let el = this.getSelectedElement();
		let index = selectables.findIndex(s => el === s);
		if (index < 0) {
			index = 0;
		} else {
			index = Math.min(Math.max(index + offset, 0), selectables.length);
		}

		Utils.selectListItem(this.el, '.selectable', 'selected', i => i === selectables[index]);
		//this.el.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
	},

	async loadData(query) {
	},
});

GW.define('StaticSuggestionsPopup', 'SuggestionsPopup', {
	
	items: [],

	'after:initComponent'() {
		this.el.classList.add('dialer-input-suggestions');
		this.el.gwReplaceChildren([{
			ref: 'list',
		}], this);
	},

	async loadData(query) {
		this.list.gwReplaceChildren(this.items.map(item => ({
			$value: item,
			nodeName: 'a',
			href: '#',
			className: 'suggestion selectable',
			textContent: item,
		})));
		return true;
	},
});

// }}}
// {{{ Password Text Field

GW.define('PasswordTextField', 'TextField', {
	type: 'password',
	autocomplete: undefined,

	'wrap:renderInput'(fn) {
		return {
			className: 'form-field password-field',
			children: [ fn() , {
				nodeName: 'button',
				type: 'button',
				ref: 'passwordToggle',
				className: 'button transparent icon-only',
				children: [Utils.useIcon(this.type == 'password' ? 'eye' : 'eyeoff')],
				'on:click': ev => this.togglePasswordVisible(),
			}]
		};
	},

	togglePasswordVisible() {
		this.input.type = this.input.type == 'password' ? 'text' : 'password';
		this.passwordToggle.gwReplaceChildren([Utils.useIcon(this.input.type == 'password' ? 'eye' : 'eyeoff')])
	}
});
// }}}

// {{{ SelectField

/*
 * There are two ways to use a SelectField:
 * 
 * 1) with a pre-loaded list of options:
 *
 *   let sel = new SelectField({options: [{text, value}, ...]})
 *   sel.setValue(value)
 *
 * 2) with list of options loaded from the server
 *
 *   let sel = new SelectField({
 *       async getOptions({signal}) {
 *           return REST.GET('options', null, {signal})
 *       },
 *   })
 *   sel.setSelectedOption({
 *     text,
 *     value,
 *   })
 *
 * The list of options does not need to be loaded from the server
 * to set a selected option value. In async mode setValue doesn't
 * work, and setSelectedOption needs to be used.
 *
 * It is possible to override onOptionSelect method, to handle
 * options selection from popup menu asynchronously.
 */
GW.define('SelectField', 'FormField', {
	options: [],
	placeholder: '',
	popupAutoWidth: true,
	returnData: false,

	renderInput() {
		return {
			ref: 'input',
			className: 'select-input ' + (this.cls || ''),
			title: this.title,

			'on:keydown': ev => {
				ev.stopPropagation();

				if (ev.key === ' ' || ev.key === 'Enter' || ev.key === 'ArrowDown') {
					this.toggleDropdown();
				}
			},

			'on:click': ev => {
				ev.stopPropagation();

				this.toggleDropdown();
			}
		};
	},

	'after:initComponent'() {
		this.setDisabled(this.disabled);

		if (this.value != null) {
			this.setValue(this.value);
		}

		if (this.autofocus && !this.disabled) {
			setTimeout(() => this.input.focus(), 10);
		}
	},

	setOptions(opts) {
		this.options = opts;

		if (this.popup) {
			this.popup.setOptions(this.renderPopupOptions(opts));
		}
	},

	onOptionSelect(o) {
		this.setSelectedOption(o);
		this.fire('change');
	},

	renderPopupOption(o) {
		if (o.render) {
			return o;
		}

		return {
			text: o.popup_text || o.text,
			cls: (o.cls ? o.cls : '') + ' ' + (o.value == this.getValue() ? 'selected' : ''),
			rightIconName: o.value == this.getValue() ? 'check' : 'empty',
			handle: () => {
				this.onOptionSelect(o);
				this.input.focus();
			},
		};
	},

	renderPopupOptions(options) {
		return options.map(o => this.renderPopupOption(o));
	},

	getOptions() {
		return this.options;
	},

	onFilterChange(menu, query, opts) {
		let match = TextUtils.createMatchAll(query || '');

                opts = opts.filter(o => match(o.text));

		menu.itemsCt.gwReplaceChildren(this.renderPopupOptions(opts).map(o => menu.renderOption(o)));
	},

	async renderPopup() {
		let signal = Utils.abortAndRetry(this, 'reload');
		let opts = await this.getOptions({signal});
		let me = this;

		this.input.classList.add('expanded');

		return new PopupMenu({
			options: this.renderPopupOptions(opts),
			'wrap:renderMenu'(origFn) {
				let els = origFn();
				if (!me.filterable) {
					return els;
				} else {
					this.el.classList.add('filterable');
					this.el.tabIndex = '-1';
				}

				return [{
					ref: 'searchEl',
					nodeName: 'input',
					type: 'search',
					placeholder: _('Hledat'),
					className: (me.input.classList.contains('small') ? 'small' : ''),
					'on:input': ev => me.onFilterChange(this, ev.target.value, opts),
				}, {
					ref: 'itemsCt',
					className: 'items',
					children: els,
				}];
			},
			focus() {
				if (this.searchEl) {
					this.searchEl.focus();
				} else {
					this.el.focus();
				}
			},
			'on:destroy': c => {
				this.popup = null;
				this.input.focus();
				this.input.classList.remove('expanded');
			},
		});
	},

	async toggleDropdown() {
		if (this.isDisabled()) {
			return;
		}

		if (!this.popup) {
			this.popup = await this.renderPopup();

			if (this.popupAutoWidth) {
				this.popup.el.style.width = this.input.offsetWidth + 'px';
			}

			PopupManager.show(this.popup, {
				anchorEl: this.input,
				forceAxis: 'vertical',
				preferReadingOrder: true,
				resizeNotFitting: true,
			});
		} else {
			PopupManager.hide(this.popup);
		}
	},

	isDisabled() {
		return !!this.disabled;
	},

	setDisabled(v) {
		this.disabled = !!v;

		if (this.labelEl) {
			this.labelEl.classList.toggle('disabled', this.disabled);
		}

		if (this.disabled && this.popup) {
			PopupManager.hide(this.popup);
		}

		this.refreshInput();
	},

	refreshInput() {
		this.input.textContent = (this.option && this.option.value != null) ? this.option.text : this.placeholder;

		this.input.classList.toggle('disabled', this.disabled);
		if (this.disabled) {
			this.input.removeAttribute('tabindex');
		} else {
			this.input.tabIndex = '0';
		}
	},

	transformOption(option) {
		return option;
	},

	setSelectedOption(option) {
		this.option = option;
		this.refreshInput();
	},

	getSelectedOption() {
		return this.option;
	},

	async setValue(value) {
		if (typeof value == 'object' && value != null) {
			return this.setSelectedOption(this.transformOption(value));
		}

		let signal = Utils.abortAndRetry(this, 'initial-load');

		this.setSelectedOption({
			value,
			text: '...',
		});

		let opts = this.getOptions({signal});
		if (opts instanceof Promise) {
			opts.then(opts => {
				this.setSelectedOption(opts.find(o => o.value == value));
			});
		} else {
			this.setSelectedOption(opts.find(o => o.value == value));
		}
	},

	getValueData() {
		let o = this.getSelectedOption();

		return o ? o.data : null;
	},

	getValue() {
		if (this.returnData) {
			return this.getValueData();
		}

		return this.option ? this.option.value : null;
	},
});

// }}}
// {{{ TextareaField

GW.define('TextareaField', 'FormField', {
	renderInput() {
		return {
			ref: 'input',
			nodeName: 'textarea',
			disabled: this.disabled,
			tabIndex: this.disabled ? '-1' : '0',
			autofocus: this.autofocus,
			placeholder: this.placeholder,
			'on:input': ev => this.fire('change'),
		};
	},

	isDisabled() {
		return this.input.disabled;
	},

	setDisabled(v) {
		this.input.disabled = !!v;
		this.input.tabIndex = v ? '-1' : '0';
	},

	setValue(value) {
		this.input.value = value == null ? '' : value;
	},

	getValue() {
		if (this.input.value.trim() === '') {
			return null;
		}

		return this.input.value;
	},
});

// }}}
// {{{ BinarySwitchField

GW.define('BinarySwitchField', 'FormField', {
	renderInput() {
		return {
			className: 'bin-switch-control',
			children: [{
				ref: 'input',
				className: 'bin-switch',
				children: [{
					className: 'bullet',
				}],
			}, {
				':skip': !this.text,
				className: 'bin-switch-label',
				textContent: this.text,
			}],
			'on:click': ev => this.toggle(),
		};
	},

	toggle() {
		this.input.dataset.state = this.input.dataset.state == "on" ? "off" : "on";
		this.fire('change');
	},

	setValue(value) {
		this.input.dataset.state = value ? "on" : "off";
	},

	getValue() {
		return this.input.dataset.state == "on";
	},
});

// }}}
// {{{ ImageUploadField

GW.define('ImageUploadField', 'FormField', {
	requiredErrorMessage: _('Obrázek je vyžadovaný'),

	renderInput() {
		return {
			ref: 'input',
			className: 'image-upload-field drop-zone',
			'on:drop': ev => {
				let file = this.fileDropHandler(ev);
				if (file) {
					if (file.size > 10000000) {
						Application.notify({
							kind: 'error',
							text: _('Limit velikosti souboru je 10MB'),
							priority: 5,
							timeout: 5000,
						});  
						return;
					}

					this.showFile(file);
					this.value = null;
					this.fire('change');
				}
			},
		};
	},

	'after:initComponent'() {
		this.refresh();
	},

	refresh() {
		this.input.textContent = '';

		if (this.fileUrl) {
			this.input.gwCreateChild({
				nodeName: 'img',
				alt: this.label,
				src: this.fileUrl,
			});

			this.input.gwCreateChild({
				className: 'controls',
				children: [{
					nodeName: 'a',
					href: '#',
					textContent: _('Odstranit obrázek'),
					'on:click': ev => {
						ev.preventDefault();
						this.showFile();
						this.value = null;
						this.fire('change');
					},
				}],
			});
		} else {
			this.input.gwCreateChild({
				nodeName: 'button',
				type: 'button',
				className: 'primary',
				textContent: _('Nahrát obrázek'),
				'on:click': ev => {
					this.doSelectFile();
				},
			});
		}
	},

	fileDropHandler(ev) {
		try {
			let files = [];

			ev.preventDefault();

			if (ev.dataTransfer.items) {
				for (let i = 0; i < ev.dataTransfer.items.length; i++) {
					if (ev.dataTransfer.items[i].kind === 'file') {
						files.push(ev.dataTransfer.items[i].getAsFile())
					}
				}
			} else {
				for (let i = 0; i < ev.dataTransfer.files.length; i++) {
					files.push(ev.dataTransfer.files[i]);
				}
			}

			if (files.length != 1) {
				Application.notify({
					kind: 'error',
					text: _('Přetáhnout lze pouze jednu fotografii.'),
					priority: 5,
					timeout: 5000,
				});  
			} else {
				return files[0];
			}
		} catch (ex) {
			console.log(ex);
		}
	},

	chooseFile() {
		return new Promise((res, rej) => {
			let fileEl = document.gwCreateElement({
				nodeName: 'input',
				type: 'file',
				accept: '.jpg,image/*',
				capture: 'environment',
				'style:display': 'none',
				'on:change': ev => {
					res(fileEl.files[0]);
					fileEl.remove();
				}
			});

			// iOS Safari requires file input to be in the DOM
			document.body.appendChild(fileEl);
			fileEl.click();
		});
	},

	showFile(file) {
		if (this.fileUrl) {
			URL.revokeObjectURL(this.fileUrl);
			this.fileUrl = null;
		}

		this.file = file;
		if (file) {
			this.fileUrl = URL.createObjectURL(file);
		}

		this.refresh();
	},

	async doSelectFile(u) {
		let file = await this.chooseFile();
		if (file.size >= 10000000) {
			Application.notify({
				kind: 'error',
				text: _('Limit velikosti souboru je 10MB'),
				priority: 5,
				timeout: 5000,
			});  
			return;
		}

		this.showFile(file);
		this.value = null;
		this.fire('change');
	},

	setValue(data) {
		this.fileUrl = data ? data.url : null;
		this.value = data;

		this.refresh();
	},

	getValue() {
		if (this.value) {
			return this.value;
		}

		if (this.file) {
			return {
				file: this.file,
			};
		}

		return null;
	},
});

// }}}
// {{{ FileContentUploadField

GW.define('FileContentUploadField', 'FormField', {
	requiredErrorMessage: _('Soubor je vyžadovaný'),
	
	contentType: 'text/plain',
	fileAccept: '*.txt',

	renderInput() {
		return {
			ref: 'input',
			className: 'file-upload-field drop-zone',
			'on:drop': async ev => {
				let file = this.fileDropHandler(ev);
				if (file) {
					await this.setValueFile(file);
					this.fire('change');
				}
			},
		};
	},

	'after:initComponent'() {
		this.refresh();
	},

	refresh() {
		this.input.textContent = '';

		if (this.value) {
			this.input.gwCreateChild({
				nodeName: 'a',
				alt: this.label,
				download: this.fileName,
				href: this.fileUrl,
				textContent: _('Stáhnout nahraný soubor'),
			});

			this.input.gwCreateChild({
				className: 'controls',
				children: [{
					nodeName: 'a',
					href: '#',
					textContent: _('Odstranit soubor'),
					'on:click': ev => {
						ev.preventDefault();
						this.setValue(null);
						this.fire('change');
					},
				}],
			});
		} else {
			this.input.gwCreateChild({
				nodeName: 'button',
				type: 'button',
				className: 'primary',
				textContent: _('Nahrát soubor'),
				'on:click': async  ev => {
					await this.doSelectFile();
				},
			});	
		}
	},

	fileDropHandler(ev) {
		try {
			let files = [];

			ev.preventDefault();

			if (ev.dataTransfer.items) {
				for (let i = 0; i < ev.dataTransfer.items.length; i++) {
					if (ev.dataTransfer.items[i].kind === 'file') {
						files.push(ev.dataTransfer.items[i].getAsFile());
					}
				}
			} else {
				for (let i = 0; i < ev.dataTransfer.files.length; i++) {
					files.push(ev.dataTransfer.files[i]);
				}
			}

			if (files.length != 1) {
				Application.notify({
					kind: 'error',
					text: _('Přetáhnout lze pouze jeden soubor.'),
					priority: 5,
					timeout: 5000,
				});  
			} else {
				return files[0];
			}
		} catch (ex) {
			console.log(ex);
		}
	},

	chooseFile() {
		return new Promise((res, rej) => {
			let fileEl = document.gwCreateElement({
				nodeName: 'input',
				type: 'file',
				accept: this.fileAccept,
				'style:display': 'none',
				'on:change': ev => {
					res(fileEl.files[0]);
					fileEl.remove();
				}
			});

			// iOS Safari requires file input to be in the DOM
			document.body.appendChild(fileEl);
			fileEl.click();
		});
	},

	async setValueFile(file) {
		if (file.size > 10000) {
			Application.notify({
				kind: 'error',
				text: _('Limit velikosti souboru je 10kB'),
				priority: 5,
				timeout: 5000,
			});
			return;
		}
		this.setValue(await new Promise((res, rej) => {
			const reader = new FileReader();
			reader.onload = e => res(e.target.result);
			reader.onerror = e => rej(e.target.error);
			reader.readAsText(file);
		}));
	},

	async doSelectFile() {
		let file = await this.chooseFile();
		await this.setValueFile(file);
		this.fire('change');
	},

	setValue(data) {
		this.value = data;

		if (this.fileUrl) {
			URL.revokeObjectURL(this.fileUrl);
		}
		this.fileUrl = null;
		if (this.value) {
			this.fileUrl = URL.createObjectURL(new Blob([this.value], {type: this.contentType}))
		}

		this.refresh();
	},

	getValue() {
		return this.value;
	},
});

// }}}
// {{{ AckCheckboxField

GW.define('AckCheckboxField', 'FormField', {
	shortText: '',
	longText: '',
	label: '',
	requiredErrorMessage: _('Aby jste mohli pokračovat musíte souhlasit s podmínkami'),

	renderInput() {
		let id = GW.uniqueId('cb-');

		return {
			className: 'ack-checkbox',
			children: [{
				className: 'check',
				children: [{
					ref: 'input',
					nodeName: 'input',
					type: 'checkbox',
					disabled: this.disabled,
					indeterminate: this.indeterminate,
					id,
					className: this.cls,
					'on:input': ev => this.fire('change'),
				}, {
					nodeName: 'label',
					htmlFor: id,
					ref: 'labelEl',
					children: this.getLabelContent(),
				}],
			}, {
				':skip': !this.longText,
				className: 'info',
				textContent: this.longText,
			}, {
				':skip': this.getLinks().length == 0,
				className: 'links',
				children: this.getLinks(),
			}]
		};
	},

	isDisabled() {
		return this.input.disabled;
	},

	setDisabled(v) {
		this.input.disabled = !!v;
	},

	getLabelContent() {
		return [this.shortText];
	},

	getLinks() {
		return [];
	},

	toggle() {
		this.input.checked = !this.input.checked;
		this.fire('change');
	},

	setValue(value) {
		this.input.checked = !!value;
	},

	getValue() {
		return this.input.checked;
	},

	isEmpty() {
		return !this.getValue();
	},
});

// }}}
// {{{ CheckboxGroup

GW.define('CheckboxGroup', 'FormField', {
	options: [],

	renderInput() {
		return {
			ref: 'input',
			className: 'checkbox-group ' + (this.cls || ''),
			children: this.options.map(o => this.renderOption(o)),
			'on*:input': ev => {
				this.fire('change');
			},
		};
	},

	'after:initComponent'() {
		this.setDisabled(this.disabled);

		if (this.value != null) {
			this.setValue(this.value);
		}
	},

	setOptions(opts) {
		this.options = opts || [];

		this.input.gwReplaceChildren(this.options.map(o => this.renderOption(o)));
	},

	renderOption(o) {
		let id = GW.uniqueId();

		return {
			className: 'check',
			children: [{
				$option: o,
				nodeName: 'input',
				type: 'checkbox',
				id,
			}, {
				nodeName: 'label',
				htmlFor: id,
				textContent: o.text,
			}],
		};
	},

	isEmpty() {
		return this.getValue().length == 0;
	},

	isDisabled() {
		return this.disabled;
	},

	setDisabled(v) {
		this.disabled = !!v;

		for (let el of this.input.querySelectorAll('input')) {
			el.disabled = this.disabled;
		}
	},

	setValue(value) {
		let enabled = new Set(Array.isArray(value) ? value : []);

		for (let el of this.input.querySelectorAll('input')) {
			el.checked = enabled.has(el.$option.value);
		}
	},

	getValue() {
		let value = [];

		for (let el of this.input.querySelectorAll('input')) {
			if (el.checked) {
				value.push(el.$option.value);
			}
		}

		return value;
	},
});

// }}}
// {{{ RadioButtonGroup

GW.define('RadioButtonGroup', 'FormField', {
	options: [],

	renderInput() {
		return {
			ref: 'input',
			className: 'radio-field ' + (this.cls || ''),
			children: this.options.map(o => this.renderOption(o)),
			'on*:input': ev => {
				this.fire('change');
			},
		};
	},

	'after:initComponent'() {
		this.setDisabled(this.disabled);

		if (this.value != null) {
			this.setValue(this.value);
		}
	},

	setOptions(opts) {
		this.options = opts || [];

		this.input.gwReplaceChildren(this.options.map(o => this.renderOption(o)));
	},

	renderOption(o) {
		let id = GW.uniqueId();

		return {
			className: 'radio-item',
			children: [{
				nodeName: 'input',
				type: 'radio',
				name: this.name,
				value: o.value,
				id,
			}, {
				nodeName: 'label',
				htmlFor: id,
				textContent: o.text,
			}],
		};
	},

	isDisabled() {
		return this.disabled;
	},

	setDisabled(v) {
		this.disabled = !!v;

		for (let el of this.input.querySelectorAll('input')) {
			el.disabled = this.disabled;
		}
	},

	setValue(value) {
		for (let el of this.input.querySelectorAll('input')) {
			el.checked = el.value == value;
		}
	},

	getValue() {
		for (let el of this.input.querySelectorAll('input')) {
			if (el.checked) {
				return el.value;
			}
		}
	},
});

// }}}
// {{{ SpinnerField

GW.define('SpinnerField', 'FormField', {
	
	integer: true,

	renderInput() {
		return {
			className: 'input-stepper',
			children: [{
				nodeName: 'button',
				type: 'button',
				children: [Utils.useIcon('minus')],
				className: 'button xxs secondary icon-only',
				'on:click': (e) => {
					let val = this.getValue();
					this.setValue(val - (this.step || 1));
					this.fire('change');
				}
			}, {
				ref: 'input',
				nodeName: 'input',
				type: 'number',
				step: this.step || 'any',
				min: this.min,
				max: this.max,
				required: !this.optional,
				disabled: this.disabled,
				readOnly: this.readOnly,
				tabIndex: this.disabled ? '-1' : '0',
				autofocus: this.autofocus,
				placeholder: this.placeholder,
				'on:blur': ev => this.onBlur(),
			}, {
				nodeName: 'button',
				type: 'button',
				children: [Utils.useIcon('plus')],
				className: 'button xxs secondary icon-only',
				'on:click': e => {
					let val = this.getValue();
					this.setValue(val + (this.step || 1));
					this.fire('change');
				}
			}]
		}
	},
	
	setValue(v) {
		if (v == null) {
			this.input.value = '';
			return;
		}
		v = Number(v);
		if (this.min != null) {
			v = Math.max(this.min, v);
		}
		if (this.max != null) {
			v = Math.min(this.max, v);
		}
		this.input.value = v;
	},
	
	getValue() {
		if (!this.input.value.trim()) {
			return null;
		}

		let num = Number(this.input.value);

		return isNaN(num) ? null : num;
	},
	
	onBlur() {
		if (this.integer) {
			let val = this.getValue();
			if (val !== null) {
				this.setValue(Math.round(val));
			}
		}
		this.fire('change');
	}

});


// }}}
// {{{ FormSegment

GW.define('FormSegment', 'GW.Component', {
	title: '',

	initComponent() {
		this.el = document.gwCreateElement({
			nodeName: 'form',
			noValidate: true,
			'on:submit': ev => {
				ev.preventDefault();
				this.submit();
			},
			className: 'form form-segment',
			'attr:data-status': 'error',
			children: [{
				className: 'header',
				children: [{
					ref: 'statusEl',
					className: 'status',
				}, {
					className: 'title',
					textContent: this.title,
				}, {
					className: 'expand',
					'on:click': ev => {
						if (this.el.classList.contains('collapsible')) {
							this.el.classList.toggle('collapsed');
						}
					},
				}],
			}, {
				className: 'fields',
				children: this.children,
			}, {
				className: 'form-buttons',
				children: [{
					ref: 'submitBtn',
					nodeName: 'button',
					type: 'submit',
					className: 'primary',
						textContent: _('Uložit změny'),
				}],
			}, {
				ref: 'progressEl',
				className: 'form-progress hidden',
				children: [{
					className: 'progress-bar',
					children: [{
						ref: 'progressIndicator',
						className: 'progress-bar-indicator',
					}],
				}],
			}],
		}, this);

		this.on('change', c => {
			this.setCollapsible(false);
			this.setStatus('error');
			this.submitBtn.disabled = false;
			this.changed = true;
		}, this, true);
	},

	/* 0..1 */
	setProgress(progress) {
		if (progress == null) {
			this.progressEl.classList.add('hidden');
		} else {
			this.progressIndicator.style.setProperty('width', Math.round(100 * progress) + '%');
			this.progressEl.classList.remove('hidden');
		}
	},

	async submit() {
		try {
			if (!this.reportFormValid()) {
				return false;
			}

			this.submitBtn.disabled = true;

			return await this.onSubmit(this);;
		} finally {
			this.submitBtn.disabled = false;
		}
	},

	onSubmit(subform) {
		return false;
	},

	collapse() {
		this.el.classList.add('collapsed');
	},

	isCollapsed() {
		return this.el.classList.contains('collapsed');
	},

	setCollapsible(v) {
		this.el.classList.toggle('collapsible', !!v);
	},

	setStatus(s) {
		this.el.dataset.status = s;
		if (s == 'ok') {
			this.setCollapsible(true);
			this.collapse();
			this.submitBtn.disabled = true;
			this.changed = false;
		}

		this.fire('statuschange');
	},

	...FormMixin,
});

// }}}
// {{{ AutoPasswordField

GW.define('AutoPasswordField', 'FormField', {
	allowUserDefined: false,

	renderInput() {
		return {
			ref: 'input',
			className: 'auto-password-field',
			children: [{
				ref: 'pwdEl',
				className: 'password',
			}, {
				className: 'copyclip transparent icon-only small',
				nodeName: 'button',
				title: _('Zkopírovat do schránky'),
				children: [Utils.useIcon('duplicate')],
				'on:click': ev => {
					ev.preventDefault();

					this.onCopyToClipboard();
				},
			}, {
				className: 'fill',
			}, {
				className: 'regenerate secondary icon-only small',
				nodeName: 'button',
				title: _('Vygenerovat nové heslo'),
				children: [Utils.useIcon('reset')],
				'on:click': ev => {
					ev.preventDefault();

					this.onRegenerate();
				},
			}, {
				':skip': !this.allowUserDefined,
				className: 'userdefined secondary icon-only small',
				nodeName: 'button',
				title: _('Nastavit vlastní heslo'),
				children: [Utils.useIcon('pencil')],
				'on:click': ev => {
					ev.preventDefault();

					this.onUserDefined();
				},
			}],
		};
	},

	onCopyToClipboard() {
		let v = this.getValue();
		if (v) {
			navigator.clipboard.writeText(v);

			this.input.classList.add('copy-success');
			Utils.timer(this, 'copied', 1500, () => {
				this.input.classList.remove('copy-success');
			});
		}
	},

	generate(chars = 30) {
		let a = new Uint8Array(chars);
		let map = '23456789abcdefghijklmnopqrstuvwxyz';
		crypto.getRandomValues(a);

		let pw = '';
		for (let n of a) {
			pw += map[n % map.length];
		}

		return pw;
	},

	onUserDefined() {
		let me = this;

		new Main.FormDialog({
			title: _('Nastavit vlastní heslo'),

			renderFormFields() {
				return [{
					xtype: 'PasswordTextField',
					name: 'password',
					label: _('Heslo'),
				}];
			},

			onSave(data) {
				me.setValue(data.password);
				me.fire('change');
			},
		});
	},

	onRegenerate() {
		this.setValue(this.generate());
		this.fire('change');
	},

	setValue(value) {
		this.pwdEl.textContent = value || '';
	},

	getValue() {
		return this.pwdEl.textContent || null;
	},
});

// }}}
// {{{ AudioUploadField

GW.define('AudioUploadField', 'FormField', {
	requiredErrorMessage: _('Audio je nutné nahrát'),
	sizeLimit: 50, // MiB

	renderInput() {
		return {
			ref: 'input',
			className: 'audio-upload-field drop-zone',
			'on:drop': ev => {
				let file = this.fileDropHandler(ev);
				if (file) {
					if (file.size > this.sizeLimit * 1024 * 1024) {
						Application.notify({
							kind: 'error',
							text: _('Limit velikosti souboru je {size} MiB')
								.replace('{size}', this.sizeLimit),
							priority: 5,
							timeout: 5000,
						});  
						return;
					}

					this.showFile(file);
					this.value = null;
					this.fire('change');
				}
			},
		};
	},

	'after:initComponent'() {
		this.refresh();
	},

	refresh() {
		this.input.textContent = '';

		if (this.fileUrl) {
			this.input.gwCreateChild({
				nodeName: 'audio',
				controls: true,
				src: this.fileUrl,
			});

			this.input.gwCreateChild({
				className: 'controls',
				children: [{
					nodeName: 'button',
					type: 'button',
					textContent: _('Odstranit audio'),
					'on:click': ev => {
						ev.preventDefault();
						this.showFile();
						this.value = null;
						this.fire('change');
					},
				}],
			});
		} else {
			this.input.gwCreateChild({
				className: 'buttons',
				ref: 'buttonRow',
				children: [{
					nodeName: 'button',
					type: 'button',
					className: 'secondary icon-left',
					children: [Utils.useIcon('upload'), _('Nahrát soubor (wav, ogg/vorbis, mp3, ...)')],
					'on:click': ev => {
						this.doSelectFile();
					},
				}, {
					nodeName: 'button',
					type: 'button',
					className: 'secondary icon-left',
					children: [Utils.useIcon('dot'), _('Záznam')],
					'on:click': ev => {
						this.startRecording(ev.target.closest('button'));
					},
				}],
			}, this);

			this.input.gwCreateChild({
				className: 'dnd-note',
				textContent: _('Nebo sem soubor přetáhněte'),
			});
		}
	},

	async startRecording() {
		const buttonRow = document.gwCreateElement({
			className: 'buttons',
			ref: 'recordingButtonRow',
			children: [{
				nodeName: 'button',
				type: 'button',
				className: 'error-secondary icon-both static',
				children: [Utils.useIcon('dot'), {textContent: _('Náhrávám'), className: 'text-body font-weight-bolder'}, {
					nodeName: 'img',
					src: APP_BASE + 'img/dialing-animation.gif',
					className: 'icon',
				}],
			}, {
				ref: 'stopBtn',
				nodeName: 'button',
				type: 'button',
				className: 'error icon-left',
				children: [Utils.useIcon('microphoneoff'), {textContent: _('Ukončit náhrávání')}],
			}],
		}, this);

		this.buttonRow.gwHide(true);
		this.buttonRow.after(buttonRow);

		try {
			let stream = await navigator.mediaDevices.getUserMedia({audio: true});
			let rec = new MediaRecorder(stream);

			rec.ondataavailable = ev => {
				this.showFile(ev.data);
			};

			rec.start();

			this.stopBtn.addEventListener('click', ev => {
				rec.stop();
			});
		} catch (ex) {
			buttonRow.remove();
			this.buttonRow.gwHide(false);
			throw ex;
		}
	},

	fileDropHandler(ev) {
		let files = [];

		ev.preventDefault();

		if (ev.dataTransfer.items) {
			for (let i = 0; i < ev.dataTransfer.items.length; i++) {
				if (ev.dataTransfer.items[i].kind === 'file') {
					files.push(ev.dataTransfer.items[i].getAsFile())
				}
			}
		} else {
			for (let i = 0; i < ev.dataTransfer.files.length; i++) {
				files.push(ev.dataTransfer.files[i]);
			}
		}

		if (files.length != 1) {
			Application.notify({
				kind: 'error',
				text: _('Přetáhnout sem lze pouze přesně jeden audio soubor.'),
				priority: 5,
				timeout: 5000,
			});  
		} else {
			return files[0];
		}
	},

	chooseFile() {
		return new Promise((res, rej) => {
			let fileEl = document.gwCreateElement({
				nodeName: 'input',
				type: 'file',
				accept: '.mp3,.ogg,.wav,audio/*',
				capture: 'environment',
				'style:display': 'none',
				'on:change': ev => {
					res(fileEl.files[0]);
					fileEl.remove();
				}
			});

			// iOS Safari requires file input to be in the DOM
			document.body.appendChild(fileEl);
			fileEl.click();
		});
	},

	showFile(file) {
		if (this.fileUrl) {
			URL.revokeObjectURL(this.fileUrl);
			this.fileUrl = null;
		}

		this.file = file;
		if (file) {
			this.fileUrl = URL.createObjectURL(file);
		}

		this.refresh();
	},

	async doSelectFile(u) {
		let file = await this.chooseFile();
		if (file.size >= this.sizeLimit * 1024 * 1024) {
			Application.notify({
				kind: 'error',
				text: _('Limit velikosti souboru je {size} MiB')
					.replace('{size}', this.sizeLimit),
				priority: 5,
				timeout: 5000,
			});  
			return;
		}

		this.showFile(file);
		this.value = null;
		this.fire('change');
	},

	isEmpty() {
		return !this.file && !this.value;
	},

	async commit() {
		if (!this.file) {
			return;
		}

		let signal = Utils.abortAndRetry(this, 'upload');
		let data = await Uploads.uploadFile('files?convert_audio=1', this.file, {signal});

		this.setValue({
			url: '/api/files/' + data.id + '/data',
			id: data.id,
		});
	},

	setValue(data) {
		if (typeof data == 'object' && data) {
			this.fileUrl = data.url;
			this.value = data.id;
		} else {
			this.fileUrl = null;
			this.value = undefined;
		}

		this.refresh();
	},

	getValue() {
		return this.value;
	},
});

// }}}
// {{{ RangeField

GW.define('RangeField', 'FormField', {
	step: 1,
	min: 0,
	max: 100,

	renderInput() {
		return {
			ref: 'input',
			nodeName: 'input',
			type: 'range',
			'on:input': () => this.fire('change'),
			min: this.min,
			max: this.max,
			step: this.step,
			disabled: this.disabled,
			autofocus: this.autofocus,
			className: this.className,
		};
	},

	isDisabled() {
		return this.input.disabled;
	},

	setDisabled(v) {
		this.input.disabled = !!v;
	},

	setValue(value) {
		this.input.value = value == null ? '' : value;
	},

	getValue() {
		return this.input.value;
	},
});

// }}}
// {{{ ParsedField

GW.define('ParsedField', 'TextField', {
	parser: null,
	parserRule: null,
	formatData: null,

	'after:initComponent'() {
		this.inputCt.gwCreateChild({
			ref: 'errMask',
			className: 'error-mask',
		}, this);

		this.inputCt.gwCreateChild({
			ref: 'errMark',
			className: 'error-marker',
		}, this);

		this.inputCt.gwCreateChild({
			ref: 'errOver',
			className: 'error-overlay',
		}, this);

		this.errMark.gwHide(true);
		this.errOver.gwHide(true);

		this.inputCt.className = 'error-masked-input';

		this.input.addEventListener('input', ev => {
			this.updateErrMask();
		});
	},

	initErrMask() {
		let s = getComputedStyle(this.input);
		for (let n of s) {
			if (!['top', 'left', 'position', 'visibility', 'pointer-events'].includes(n)) {
				this.errMask.style[n] = s.getPropertyValue(n);
			}
		}
	},

	getParserOptions() {
		return {};
	},

	parse(val) {
		return this.parser.parse(val, {...this.getParserOptions(), startRule: this.parserRule});
	},

	updateErrMask() {
		let val = this.input.value;
		if (val.trim() === '') {
			this.errMark.gwHide(true);
			this.errOver.gwHide(true);
			return;
		}

		try {
			this.parse(val);

			this.errMark.gwHide(true);
			this.errOver.gwHide(true);
		} catch (ex) {
			if (!this.maskReady) {
				this.maskReady = true;
				this.initErrMask();
			}

			let text = document.createTextNode(val);
			this.errMask.textContent = '';
			this.errMask.append(text);
			let range = document.createRange();
			range.setStart(text, ex.location.start.offset);
			range.setEnd(text, ex.location.end.offset);

			let rs = this.errMask.getBoundingClientRect();
			let re = range.getBoundingClientRect();
			let pos = {
				top: re.top - rs.top - 3,
				left: re.left - rs.left,
				width: re.width,
				height: re.height + 2,
			};

			this.errMark.style.top = pos.top + 'px';
			this.errMark.style.left = pos.left + 'px';
			this.errMark.style.width = pos.width + 'px';
			this.errMark.style.height = pos.height + 'px';
			this.errMark.gwHide(false);

			this.errOver.textContent = ex.message;
			this.errOver.style.top = (pos.top + pos.height) + 'px';
			this.errOver.style.left = pos.left + 'px';
			this.errOver.gwHide(false);
		}
	},

	getViolation() {
		let val = this.input.value.trim();
		if (val === '') {
			return this.optional ? null : this.requiredErrorMessage;
		}

		this.updateErrMask();

		try {
			this.parse(val);
		} catch (ex) {
			return ex.message;
		}
	},

	getValue() {
		let val = this.input.value.trim();

		try {
			if (this.formatData) {
				return this.parse(val);
			} else {
				this.parse(val);
				return v;
			}
		} catch (ex) {
			return null;
		}
	},

	setValue(v) {
		if (this.formatData) {
			this.input.value = this.formatData(v);
		} else {
			this.input.value = v != null ? v : '';
		}
	},
});

// }}}
// {{{ TabbedFormFieldsGroups

GW.define('TabbedFormFieldsGroups', 'GW.Component', {
	initComponent() {
		let tabs = this.getTabPanels();

		this.el = document.gwCreateElement({
			children: [{
				ref: 'toolbarEl',
				className: 'toolbar',
				children: [{
					ref: 'tabs',
					xtype: 'Widgets.TabSwitcher',
					'on:beforeselect': (c, i) => this.switchTab(i),
					items: tabs,
				}],
			}, {
				ref: 'panelsEl',
				children: tabs,
			}],
		}, this);

		let first = tabs[0];

		first && this.switchTab(first);
	},

	getTabPanels() {
		return [];
	},

	switchTab(tab) {
		this.tabs.setSelected(tab.id);

		for (let el of this.panelsEl.children) {
			el.gwHide(el.gwComponent.id != tab.id);
		}
	},

	highlightError() {
		// Find first invalid field
		let field = this.traverseChildren(f => {
			if (f.isDisabled && f.isDisabled()) {
				return 'skip';
			}

			if (f instanceof TabbedFormFieldsGroups) {
				if (f.highlightError()) {
					return 'return';
				}

				return 'skip';
			}

			if (f.validate) {
				if (!f.validate()) {
					return 'return';
				}

				return 'skip';
			}

			return null;
		});

		if (field) {
			// From the found field go up the ancestors tree and
			// activate relevant tabs
			while (field && field != this) {
				let p = field.getParent();
				if (p == this && field instanceof FormFieldsGroup) {
					this.switchTab(field);
					return true;
				}

				field = p;
			}
		}

		return false;
	},
});

// }}}
// {{{ FormFieldsGroup

GW.define('FormFieldsGroup', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			children: this.renderFormFields(),
		}, this);
		
		this.el.classList.addFromStringList(this.className);
	},

	renderFormFields() {
		return [];
	},

	setDisabled(disabled) {
		this.disabled = !!disabled;
	},

	isDisabled() {
		return this.disabled;
	},

	getValue() {
		return this.getFormFieldsData();
	},

	setValue(v) {
		if (typeof v == 'object' && v != null) {
			this.setFormFieldsData(v);
		}
	},

	...FormMixin,
});

// }}}
